import { useState } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { useCurrentUser } from '@/hooks/use-current-user';
import { canAccessKaiyaSection } from '@/lib/kaiya-access';
import {
  Sidebar as SidebarUI,
  SidebarContent,
  SidebarMenu,
  SidebarMenuItem,
  SidebarMenuButton,
  SidebarGroup,
  SidebarGroupContent,
} from 'prosci-ui';
import {
  getNavigationItems,
  isNavigationGroup,
  isNavigationSection,
  type NavigationEntry,
  type NavigationGroup,
} from './sidebar-navigation';
import { ChevronDown, ChevronRight } from '@/lib/icons';

function getAllItemsFromGroup(group: NavigationGroup) {
  const items: { href: string }[] = [];
  for (const entry of group.entries) {
    if (isNavigationSection(entry)) {
      items.push(...entry.items);
    } else {
      items.push(entry);
    }
  }
  return items;
}

function GroupNavItem({ item }: { item: { name: string; href: string; icon: React.ComponentType<{ className?: string }> } }) {
  const location = useLocation();
  const isActive =
    location.pathname === item.href ||
    (item.href !== '/' && location.pathname.startsWith(item.href + '/'));

  return (
    <SidebarMenuItem>
      <SidebarMenuButton asChild isActive={isActive}>
        <NavLink to={item.href}>
          <item.icon />
          <span>{item.name}</span>
        </NavLink>
      </SidebarMenuButton>
    </SidebarMenuItem>
  );
}

function CollapsibleNavGroup({ group }: { group: NavigationGroup }) {
  const location = useLocation();
  const allItems = getAllItemsFromGroup(group);
  const isAnyChildActive = allItems.some(
    (item) =>
      location.pathname === item.href ||
      (item.href !== '/' && location.pathname.startsWith(item.href + '/'))
  );
  const [isOpen, setIsOpen] = useState(isAnyChildActive);

  return (
    <SidebarMenuItem>
      <SidebarMenuButton
        onClick={() => setIsOpen(!isOpen)}
        isActive={isAnyChildActive && !isOpen}
        className="cursor-pointer"
      >
        <group.icon />
        <span>{group.name}</span>
        {isOpen ? (
          <ChevronDown className="ml-auto h-4 w-4" />
        ) : (
          <ChevronRight className="ml-auto h-4 w-4" />
        )}
      </SidebarMenuButton>
      {isOpen && (
        <SidebarMenu className="ml-4 mt-1 gap-0.5">
          {group.entries.map((entry) => {
            if (isNavigationSection(entry)) {
              return (
                <div key={entry.label}>
                  <span className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground/60 px-2 pt-3 pb-1 block">
                    {entry.label}
                  </span>
                  {entry.items.map((item) => (
                    <GroupNavItem key={item.name} item={item} />
                  ))}
                </div>
              );
            }
            return <GroupNavItem key={entry.name} item={entry} />;
          })}
        </SidebarMenu>
      )}
    </SidebarMenuItem>
  );
}

function NavEntry({ entry }: { entry: NavigationEntry }) {
  const location = useLocation();

  if (isNavigationGroup(entry)) {
    return <CollapsibleNavGroup group={entry} />;
  }

  const isActive =
    location.pathname === entry.href ||
    (entry.href !== '/' && location.pathname.startsWith(entry.href + '/'));

  return (
    <SidebarMenuItem>
      <SidebarMenuButton asChild isActive={isActive}>
        <NavLink to={entry.href}>
          <entry.icon />
          <span>{entry.name}</span>
        </NavLink>
      </SidebarMenuButton>
    </SidebarMenuItem>
  );
}

export function Sidebar() {
  const { data: currentAdmin } = useCurrentUser();

  // Check user types
  const isSuperAdmin = currentAdmin?.type === 'prosci_admin' && currentAdmin?.level === 'super';
  const isProsciAdmin = currentAdmin?.type === 'prosci_admin';
  const isLicenseAdmin = currentAdmin?.type === 'license_admin';
  const isDataLevel = currentAdmin?.type === 'prosci_admin' && currentAdmin?.level === 'data';
  const isAffiliate = currentAdmin?.type === 'prosci_admin' && currentAdmin?.level === 'affiliate';
  const canAccessKaiya = canAccessKaiyaSection(currentAdmin);

  // Get navigation items based on user role
  const navigation = getNavigationItems({
    isSuperAdmin,
    isProsciAdmin,
    isLicenseAdmin,
    isDataLevel,
    isAffiliate,
    canAccessKaiya,
  });

  return (
    <SidebarUI collapsible="offcanvas" className="w-[255px] border-r border-gray-200">
      {/* Navigation */}
      <SidebarContent>
        <SidebarGroup>
          <SidebarGroupContent>
            <SidebarMenu className="gap-2">
              {navigation.map((entry) => (
                <NavEntry key={isNavigationGroup(entry) ? entry.name : entry.name} entry={entry} />
              ))}
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>
      </SidebarContent>
    </SidebarUI>
  )
}
